<template>
  <WFApp
    id="app"
    class="h-full w-full flex portrait:flex-col justify-between overflow-hidden"
    @screensaving="screensaving"
  >
    <PortraitHeader class="h-92 md:h-128" v-if="portrait" :settings="settings"></PortraitHeader>
    <MapPage class="basis-2/3 portrait:basis-3/5 max-sm:basis-1/3 overflow-hidden" @loaded="mapLoaded"></MapPage>
    <div class="sidebar flex flex-col basis-1/3 portrait:basis-2/5 max-sm:basis-2/3 overflow-hidden">
      <PortraitMenu v-if="portrait"></PortraitMenu>
      <LandscapeHeader class="h-1/4" v-if="landscape" :settings="settings"></LandscapeHeader>
      <div class="h-3/4 portrait:h-full bg-base overflow-hidden flex landscape:flex-col">
        <TabMenu class="portrait:basis-1/3 overflow-hidden portrait:h-full hidden md:flex"></TabMenu>
        <WFTabs :activeTab="currentTab" class="grow portrait:basis-2/3 portrait:border-r th-border portrait:pt-18">
          <WFTab
            name="grouppois"
            class="text-xl text-secondary-content pl-32 pr-12 portrait:pr-24"
          >
            <GroupMenu
              :currentGroup="currentGroup"
              @onPOIClick="poiClicked"
              :currentPOI="currentPOI"
            ></GroupMenu>
          </WFTab>
          <WFTab name="search">
            <Search @clicked="poiClicked"></Search>
          </WFTab>
        </WFTabs>
      </div>
    </div>
    <WFBanner
      class="banners-screensaver portrait:hidden"
      :qrURL="qrURL"
      template="default"
      v-show="screensaver && isScreensaver('screensaver') && !mobile"
      container="screensaver"
      @clicked="hideBanner"
      @hasbanners="hasBanners"
    ></WFBanner>
    <WFBanner
      class="banners-screensaver landscape:hidden"
      :qrURL="qrURL"
      template="default"
      v-show="screensaver && isScreensaver('screensaver-portrait') && !mobile"
      container="screensaver-portrait"
      @clicked="hideBanner"
      @hasbanners="hasBanners"
    ></WFBanner>
  </WFApp>
</template>

<script>
import { mapState } from "vuex";

import { WFTabs, WFTab } from "@3dwayfinder/wayfinder-vue-components";

import MapPage from "./components/MapPage.vue";
import TabMenu from "./components/TabMenu.vue";
import GroupMenu from "./components/GroupMenu.vue";
import LandscapeHeader from "./components/LandscapeHeader.vue";
import PortraitHeader from "./components/PortraitHeader.vue";
import Search from "./components/Search.vue";
import PortraitMenu from "./components/PortraitMenu.vue";

const builtInClasses = ["columns-2", "columns-3"];

export default {
  name: "App",
  components: {
    MapPage,
    LandscapeHeader,
    PortraitHeader,
    Search,
    WFTabs,
    WFTab,
    TabMenu,
    GroupMenu,
    PortraitMenu
  },
  data() {
    return {
      currentPage: null,
      currentTab: "grouppois",
      pageSlug: "map",
      infoPage: null,
      screensaver: false,
      hasScreensaver: {},
      qrURL: "https://clients.3dwayfinder.com/Terkanten/?kiosk={kiosk}&map2DRotation={rot}",
      
      // Store settings as reactive data for better Vue reactivity
      settingsData: {
        showClock: true,
        showDate: true,
        colors: {
          primary: '#143c34',
          'primary-content': '#FFFFFF',
          secondary: '#333333',
          'secondary-content': '#FFFFFF',
          accent: '#FFFFFF',
          'accent-content': '#333333',
          'base-100': '#FFFFFF',
          'base-200': '#f4f4f4',
          'base-content': '#333333',
        }
      }
    };
  },
  computed: {
    ...mapState("wf", ["pages", "portrait", "landscape", "mobile"]),
    ...mapState(["currentPOI", "currentGroup", "searchVisible"]),
    
    // Check if we're on localhost
    isLocalhost() {
      return window.location.hostname === 'localhost' || 
             window.location.hostname === '127.0.0.1' ||
             window.location.hostname.startsWith('192.168');
    },
    
    // Return reactive settings object
    settings() {
      return this.settingsData;
    }
  },
  created() {
    if (this.$wf) {
      this.$store.wf = this.$wf;
      console.log("Wayfinder instance attached to store as $wf");
    }
    if (this.$wayfinder) {
      this.$store.wf = this.$wayfinder;
      console.log("Wayfinder instance attached to store as $wayfinder");
    }
    if (!this.$store.wf && window.wayfinder) {
      this.$store.wf = window.wayfinder;
      console.log("Wayfinder instance attached to store from window.wayfinder");
    }
    
    // Load settings after Wayfinder instance is available
    this.loadSettings();
  },
  watch: {
    pages() {
      this.$nextTick(() => {
        this.pageSlug = "screensaver";
      });
    },
    searchVisible() {
      if (this.searchVisible) {
        this.currentTab = "search";
      } else {
        this.currentTab = "grouppois";
      }
    },
    // Watch the reactive data directly
    'settingsData.colors': {
      handler() {
        this.applyColorSettings();
      },
      deep: true
    }
  },
  mounted() {
    console.log("mounted", builtInClasses);
    console.log("📋 Settings loaded:", this.settings);
    // Apply color settings when component mounts
    this.applyColorSettings();
  },
  methods: {
    // Load settings from Wayfinder or use localhost defaults
    loadSettings() {
      if (this.isLocalhost) {
        // Use localhost mock data (already set in data())
        console.log('📋 Using localhost mock settings');
        return;
      }
      
      // PRODUCTION: Load from Wayfinder
      try {
        const wf = this.$wf || this.$wayfinder || window.wayfinder;
        
        if (!wf) {
          console.warn('Wayfinder instance not found, using default settings');
          return;
        }
        
        if (!wf.settings) {
          console.warn('Wayfinder settings not available, using default settings');
          return;
        }
        
        console.log('📋 Loading settings from Wayfinder CMS');
        
        // Update reactive data
        this.settingsData.showClock = wf.settings['template.default.display-clock'] !== false;
        this.settingsData.showDate = wf.settings['template.default.display-clock'] !== false;
        
        // Update colors reactively
        this.settingsData.colors = {
          primary: wf.settings['template.color.primary'] || '#143c34',
          'primary-content': wf.settings['template.color.primary-content'] || '#FFFFFF',
          secondary: wf.settings['template.color.secondary'] || '#333333',
          'secondary-content': wf.settings['template.color.secondary-content'] || '#FFFFFF',
          accent: wf.settings['template.color.accent'] || '#FFFFFF',
          'accent-content': wf.settings['template.color.accent-content'] || '#333333',
          'base-100': wf.settings['template.color.base-100'] || '#FFFFFF',
          'base-200': wf.settings['template.color.base-200'] || '#f4f4f4',
          'base-content': wf.settings['template.color.base-content'] || '#333333',
        };
        
        console.log('Settings loaded from CMS');
        
      } catch (error) {
        console.error('Error loading Wayfinder settings:', error);
        console.warn('Using default settings');
      }
    },
    
    
    applyColorSettings() {
      const colors = this.settingsData.colors;
      const root = document.documentElement;
      
      
      Object.keys(colors).forEach(colorKey => {
        root.style.setProperty(`--color-${colorKey}`, colors[colorKey]);
      });
      
      console.log('🎨 Colors applied:', colors);
    },
    
    setPage(page) {
      this.pageSlug = page.slug;
      this.currentPage = page;

      if (page.slug === "map") {
        this.$wayfinder.run();
      }
    },
    setPageBySlug(slug) {
      this.pageSlug = slug;
    },
    infoPageClicked(page) {
      this.infoPage = page;
      this.pageSlug = "info-page";
    },
    poiClicked(poi) {
      if (poi) {
        this.$wayfinder.showFloor(poi.getFloor());
        this.$wayfinder.clearHighlights();
        this.$wayfinder.setHighlights([poi]);

        this.setPOI(poi);
        if (this.$refs.map) {
          this.$nextTick(() => {
            this.$refs.map.showPOIPopup(poi);
          });
        }
      }
      console.log("wf", this.$wayfinder);
    },
    setPOI(poi) {
      this.$store.dispatch("setPOI", poi);
    },
    screensaving() {
      this.currentPage = null;
      this.infoPage = null;
      this.$store.dispatch("showSearch", false);
      this.pageSlug = "grouppois";
      this.$store.dispatch("setPOI", null);
      this.$wayfinder.pause();
      this.screensaver = true;
    },
    mapLoaded() {
      var shops =  this.$wayfinder.getGroupWithSlug("shops");
      document.title = "Letipea";

      if (shops) {
        this.$store.dispatch(
          "setDefaultGroup",
          this.$store.getters["wf/freezeGroup"](shops)
        );

        console.log('def', this.$wayfinder.getGroupWithSlug("shops"))
        this.$store.dispatch(
          "setGroup",
          this.$store.getters["wf/freezeGroup"](shops)
        );
      }
      this.$wayfinderApp.loadPages();
    },
    hasBanners(has, template, container) {
      this.hasScreensaver[container] = has;
    },
    isScreensaver(container) {
      return this.hasScreensaver[container];
    },
    hideBanner() {
      this.screensaver = false;
    },
    touched() {
      this.$store.dispatch("pushLog", "Touch");
    },
  },
};
</script>


<style lang="postcss">
.wf-page-menu {
  .wf-list {
    display: flex;
    height: 100%;
    width: 100%;
    justify-content: space-evenly;
  }

  .wf-list-item {
    max-width: 20rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-grow: 1;
    height: 100%;
    font-size: 1.5rem;
    text-transform: uppercase;
    font-weight: 500;
    justify-content: center;

    &.wf-active {
      background-color: var(--th-primary);
      color: var(--th-primary-content);
    }
  }

  svg {
    height: 8rem;
    margin-bottom: 2.5rem;
  }
}

.info-menu .wf-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  margin: auto;
  grid-column-gap: 1rem;
  grid-row-gap: 1rem;
  font-size: 2.4rem;
  width: min-content;
}

.info-menu {
  padding: 2rem;
}

/* Floor buttons - correct selector for list items */
li.wf-list-item[class*="wf-floor"] {
    background-color: white !important;
    color: #333333 !important;
    border: 1px solid #e5e5e5 !important;
}

li.wf-list-item[class*="wf-floor"].wf-active {
    background-color: var(--color-primary) !important;
    color: white !important;
    border-color: var(--color-primary) !important;
}

/* Also target the container if needed */
.wf-floor-selector li,
.wf-floors-menu li {
    background-color: white !important;
    color: #333333 !important;
}

.wf-floor-selector li.wf-active,
.wf-floors-menu li.wf-active {
    background-color: var(--color-primary) !important;
    color: white !important;
}

.info-menu .wf-list-item {
  background-color: white;
  display: flex;
  min-width: 32ch;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  font-size: 2rem;
  padding: 1em;
}

.info-menu .wf-icon {
  width: 6rem;
  height: 6rem;
  margin-right: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.info-menu .wf-icon svg {
  height: 6rem;
  margin-bottom: 0;
}

.banners-screensaver {
  position: absolute !important;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  background-image: url("./assets/logo.png");
  background-size: 20%;
  background-position: 50% 50%;
  background-repeat: no-repeat;
  background-color: white;
  z-index: 99;
}

/* Wayfinder floor change text - responsive sizing */
.wf-map-path-text {
    font-size: 1.6rem !important; /* Smaller for mobile */
    font-weight: 600 !important;
    line-height: 1.4 !important;
}

/* Bigger text for tablet/portrait and desktop/landscape */
@media (min-width: 768px) {
    .wf-map-path-text {
        font-size: 2.4rem !important;
    }
}

/* Force group menu text colors to be visible */
.tabmenu .wf-list-item {
  color: var(--color-base-content) !important;
}

.tabmenu .wf-list-item.wf-active {
  color: var(--color-primary-content) !important;
}

/* Fix POI list items text and background colors */
.th-list .wf-poi-list-item,
.th-list .wf-list-item {
  color: var(--color-base-content) !important;
  background-color: var(--color-base-100) !important;
}

.th-list .wf-poi-list-item:hover,
.th-list .wf-poi-list-item.wf-active,
.th-list .wf-list-item:hover,
.th-list .wf-list-item.wf-active {
  color: var(--color-accent-content) !important;
  background-color: var(--color-accent) !important;
}

/* Also target any nested content */
.th-list .wf-poi-list-item * {
  color: inherit !important;
}
/* ===== 4K PORTRAIT - Search Bar (Universal) ===== */
@media (orientation: portrait) and (min-width: 2160px) {
  /* Target ALL search inputs in portrait */
  input[type="text"],
  input[type="search"],
  .wf-search-input,
  #search-output,
  input.th-shadow {
    height: 12.8rem !important;
    min-height: 12.8rem !important;
    font-size: 4rem !important;
    padding: 2.4rem !important;
    border-width: 2px !important;
  }
  
  /* Search icon/button next to input */
  button[type="submit"],
  .search-button,
  button.th-shadow {
    width: 12.8rem !important;
    height: 12.8rem !important;
  }
  
  /* Icons inside search */
  input + button svg,
  input + div svg,
  .search-icon {
    width: 7.4rem !important;
    height: 8.4rem !important;
  }
}
</style>